import { InvalidTimeFormatError } from '../errors'

const MINUTES_PER_HOUR = 60
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR
const QUARTER_MINUTES = 15

const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * Rounds a time string up to the next quarter hour.
 *
 * - Input must be "HH:MM" or "HH:MM:SS".
 * - Seconds in the input are ignored for rounding purposes.
 * - Output is always "HH:MM:SS" with seconds set to "00".
 * - Times at or past 23:46 wrap to "00:00:00".
 *
 * Why: the V1 platform bills working hours in quarter-hour increments,
 * always rounded up in favor of the driver (per collective agreement).
 *
 * @throws {InvalidTimeFormatError} when the input is not a valid HH:MM[:SS] string.
 */
export function roundUpQuarter(time: string): string {
  const { hours, minutes } = parseTime(time)
  const totalMinutes = hours * MINUTES_PER_HOUR + minutes
  const roundedMinutes = (Math.ceil(totalMinutes / QUARTER_MINUTES) * QUARTER_MINUTES) % MINUTES_PER_DAY

  return formatTime(roundedMinutes)
}

function parseTime(time: string): { hours: number; minutes: number } {
  const match = TIME_PATTERN.exec(time)
  if (match === null) {
    throw new InvalidTimeFormatError(time)
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])

  if (hours > 23 || minutes > 59) {
    throw new InvalidTimeFormatError(time)
  }

  return { hours, minutes }
}

function formatTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR)
  const minutes = totalMinutes % MINUTES_PER_HOUR
  return `${pad(hours)}:${pad(minutes)}:00`
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}
